import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Loader2, Globe } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { t, i18n } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@iqeslowvoltage.com");
  const [password, setPassword] = useState("IqesAdmin2026!");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || t("auth.error"));
    } finally {
      setLoading(false);
    }
  };

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language?.startsWith("es") ? "en" : "es");
  };

  return (
    <div
      className="min-h-screen grid md:grid-cols-2 bg-[#F5F7FA]"
      data-testid="login-page"
    >
      {/* Left panel: brand + blueprint grid */}
      <div className="relative hidden md:flex flex-col justify-between p-10 text-white overflow-hidden"
           style={{background: "linear-gradient(135deg, #003366 0%, #002244 60%, #001A33 100%)"}}>
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="absolute -right-40 -bottom-40 w-[500px] h-[500px] rounded-full bg-[#2DD4BF]/20 blur-3xl" />
        <div className="absolute right-10 top-10 w-[300px] h-[300px] rounded-full bg-[#EA580C]/10 blur-3xl" />
        <div className="relative z-10">
          <Logo className="text-white [&_span]:text-white [&_.text-slate-500]:text-slate-300" />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#EA580C] mb-4">
            AI ESTIMATOR · ELECTRICAL · LOW VOLTAGE
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">
            AI Estimator · Electrical · Low Voltage
          </h1>
          <p className="mt-6 text-slate-300 text-sm leading-relaxed">
            Blueprints, site surveys, photos — one AI workspace generates BOMs,
            routing, labor estimates, and NEC / TIA / BICSI compliance in seconds.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 text-sm">
            <div>
              <div className="font-display font-bold text-2xl text-white">13+</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Years</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-white">280+</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Projects</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-[#2DD4BF]">NEC 2026</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Compliant</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
          IQES LLC · FL · © 2026 · Part of IQES ecosystem: CRM + IQ-APP + ERP
        </div>
      </div>

      {/* Right panel: form */}
      <div className="flex flex-col">
        <div className="flex justify-end p-4">
          <button
            onClick={toggleLang}
            data-testid="toggle-language-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-mono uppercase tracking-wider text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Globe size={13} strokeWidth={1.75} />
            {i18n.language?.startsWith("es") ? "ES" : "EN"}
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                IQES IQ-APP · LOGIN
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
                {t("auth.welcome")}
              </h2>
              <p className="text-sm text-slate-600 mt-1">{t("auth.subtitle")}</p>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
                  {t("auth.email")}
                </label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
                  {t("auth.password")}
                </label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono"
                />
              </div>
              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-mono"
                >
                  {error}
                </div>
              )}
              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2.5 font-medium text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {t("auth.submit")}
              </button>

              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                  {i18n.language?.startsWith("es") ? "o" : "or"}
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <button
                type="button"
                onClick={startGoogleLogin}
                data-testid="login-google-btn"
                className="w-full bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 rounded-xl px-4 py-2.5 font-medium text-sm transition-colors flex items-center justify-center gap-3 shadow-sm"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2 14-5.4l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.4-11.3-8L6.3 32C9.6 38.3 16.3 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l.1.1 6.5 5.5c-.5.4 7.3-5.2 7.3-15.2 0-1.3-.1-2.3-.4-3.5z"/>
                </svg>
                <span className="text-slate-700">
                  {i18n.language?.startsWith("es") ? "Continuar con Google" : "Continue with Google"}
                </span>
              </button>

              <div className="flex items-center justify-between text-xs font-mono pt-2">
                <a href="/signup" data-testid="link-signup" className="text-[#003366] hover:underline">
                  {i18n.language?.startsWith("es") ? "Crear cuenta" : "Create account"}
                </a>
                <a href="/forgot-password" data-testid="link-forgot" className="text-slate-600 hover:text-[#003366]">
                  {i18n.language?.startsWith("es") ? "¿Olvidaste tu contraseña?" : "Forgot password?"}
                </a>
              </div>
              <div className="text-[11px] text-slate-500 font-mono mt-4 border-t border-slate-200 pt-4">
                {t("auth.demo")}
              </div>
            </form>
          </div>
        </div>
        <div className="p-4 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
          Part of the IQES ecosystem · CRM · IQ-APP · ERP
        </div>
      </div>
    </div>
  );
}
